/** @jsxImportSource @emotion/react */
import { css, Theme } from '@emotion/react';
import { FC } from 'react';
import Carousel from 'react-multi-carousel';

import Text from 'components/styled/Text';
import { Flex, Box } from 'components/styled';
import { ResponsivePadder } from 'components/styled/Wrapper';

import 'react-multi-carousel/lib/styles.css';

import testimonial1 from 'modules/public/homepage/zlin/testimonial_kubalos.jpg';
import testimonial2 from 'modules/public/homepage/zlin/testimonial_kocica.jpg';
import testimonial3 from 'modules/public/homepage/zlin/testimonial_zurkova.jpg';
import testimonial4 from 'modules/public/homepage/zlin/testimonial_brhlova.jpg';

import { EASMultilanguage } from 'utils/EASTypes';
import { useLocalizedMultistring } from 'utils/localization';

type TTestimonial = {
	photo: string;
	text: EASMultilanguage;
	name: string;
	title: EASMultilanguage;
};

const testimonials: TTestimonial[] = [
	{
		photo: testimonial1,
		text: {
			CZECH:
				'JobCentrum UTB mi pomohlo nájsť moju prvú prácu na plný úväzok. Veľmi si cením aktívny prístup ku mne, prešli so mnou všetko od oblečenia na pohovor, prípravu naň až po podpísanie zmluvy a nástup do práce.',
			ENGLISH:
				'TBU JobCentrum helped me find my first full-time job. I really appreciate the active approach to me, they went through everything with me from clothes for the interview, preparation for it to signing the contract and starting work.',
		},
		title: {
			CZECH: 'absolvent FHS',
			ENGLISH: 'FHS graduate',
		},
		name: 'Matej Kubaloš',
	},
	{
		photo: testimonial2,
		text: {
			CZECH:
				'Absolvoval jsem stáž na technicko-provozním oddělení UTB, byla to unikátní příležitost, jak poznat akademické prostředí z pohledu facility managementu.',
			ENGLISH:
				'I completed an internship at the technical-operational department of TBU, it was a unique opportunity to get to know the academic environment from the point of view of facility management.',
		},
		title: {
			CZECH: 'student FAME',
			ENGLISH: 'FAME student',
		},
		name: 'Jaroslav Kočica',
	},
	{
		photo: testimonial3,
		text: {
			CZECH:
				'Využila jsem odborné rady a pomoc s mým životopisem. Myslela jsem si, že na něm není nic zle, ale právě naopak. Nebylo na něm skoro nic dobře. Bylo mi vysvětleno, jak CV seskládat, co do něj rozhodně nevkládat či jaké fotografie se do CV hodí. Využila jsem také nabídku prací, které Job Centrum zprostředkovává. Pan Prokop mi se vším nesmírně pomohl a hlavně šlo poznat, že studentům pomáhá rád.',
			ENGLISH:
				'I used professional advice and help with my resume. I thought there was nothing wrong with it, but quite the opposite. There was almost nothing good about it. I was explained how to put the CV together, what not to put in it or what photos fit into the CV. I also used the offer of work that the Job Center mediates. Mr. Prokop helped me immensely with everything and most importantly it was clear that he likes to help students.',
		},
		title: {
			CZECH: 'studentka FHS',
			ENGLISH: 'FHS student',
		},
		name: 'Lucie Žůrková',
	},
	{
		photo: testimonial4,
		text: {
			CZECH:
				'Výběrové řízení nanečisto bylo pro mě velmi přínosné. Vyzkoušela jsem si celý proces výběrového řízení a získala jsem od personalistů hodnotnou zpětnou vazbu. Tento projekt mi dal nejenom užitečné rady a tipy, ale i cenné kontakty, které možná v budoucnu využiji.',
			ENGLISH:
				'The selection procedure was very beneficial for me. I tried the whole selection process and received valuable feedback from the HR. This project gave me not only useful advice and tips, but also valuable contacts that I may use in the future.',
		},
		title: {
			CZECH: 'studentka FaME',
			ENGLISH: 'FaME student',
		},
		name: 'Jana Brhlová',
	},
];

const Testimonial: FC<TTestimonial> = ({ photo, text, name, title }) => {
	const localize = useLocalizedMultistring();

	return (
		<ResponsivePadder
			key={name}
			py={5}
			flexDirection={['column', 'row']}
			justifyContent="center"
			alignItems="center"
		>
			<Flex>
				<Box
					width={175}
					height={175}
					css={css`
						border: 6px solid white;
					`}
				>
					<img src={photo} alt={`Foto ${name}`} width="100%" />
				</Box>
			</Flex>
			<Flex mt={[4, 0]} ml={[0, 5]} width={[1, 1 / 2]} flexDirection="column">
				<Text
					css={css`
						font-style: italic;
					`}
				>
					“{localize(text)}”
				</Text>
				<Text alignSelf="flex-end">
					{'⎯⎯'} {name} ({localize(title)})
				</Text>
			</Flex>
		</ResponsivePadder>
	);
};

const responsive = {
	devices: {
		breakpoint: { max: 99999, min: 0 },
		items: 1,
	},
};

const TestimonialCarousel: FC = () => (
	<Carousel
		responsive={responsive}
		showDots
		infinite
		autoPlay
		autoPlaySpeed={5000}
		removeArrowOnDeviceType={'mobile'}
		css={(theme: Theme) => css`
			.react-multi-carousel-dot button {
				margin: 0px 8px;
				border-color: ${theme.colors.textLight};
				background-color: ${theme.colors.textLight};
			}

			.react-multi-carousel-dot--active button {
				border-color: ${theme.colors.primary};
				background-color: ${theme.colors.primary};
			}
		`}
	>
		{testimonials.map(t => Testimonial(t))}
	</Carousel>
);
export default TestimonialCarousel;
